import { getOrganizationDetailsLink } from '@/utils/organizations';
import { ATLAS_APP_BASE_URL } from '@/constants';

const redirectToOrg = (to) => {
    window.location.href = getOrganizationDetailsLink(to.params.id);
    return false;
};

const routes = [
    {
        path: '/',
        name: 'dashboard',
        component: () => import(/* webpackChunkName: "dashboard" */ '../views/Dashboard.vue'),
        meta: {
            breadcrumb: {
                parent: null, // Root level
            },
        },
    },
    {
        path: '/profile-setup',
        name: 'profile-setup',
        component: () => import(/* webpackChunkName: "profile-setup" */ '../views/ProfileSetupNew.vue'),
    },
    {
        path: '/organization/:id',
        name: 'organization-profile',
        component: () => import(/* webpackChunkName: "organization-profile" */ '../views/EmployerProfilePublic.vue'),
        meta: {
            breadcrumb: {
                parent: 'browse-organizations',
            },
        },
        beforeEnter: redirectToOrg,
    },
    {
        path: '/volunteer/:id',
        name: 'volunteer-profile',
        component: () => import(/* webpackChunkName: "volunteer-profile" */ '../views/EmployeeProfilePublic.vue'),
        meta: {
            breadcrumb: {
                parent: 'browse-volunteers',
            },
        },
    },
    {
        path: '/asset/:id',
        name: 'asset-details',
        component: () => import(/* webpackChunkName: "asset" */ '../views/AssetDetails.vue'),
        meta: {
            breadcrumb: {
                parent: 'browse-content',
            },
        },
        beforeEnter: (to) => {
            const { id } = to.params;
            window.location.href = `${ATLAS_APP_BASE_URL}/player/${id}`;
        },
    },
    {
        path: '/opportunity/new',
        name: 'new-opportunity',
        component: () => import(/* webpackChunkName: "asset" */ '../views/careershares/NewCareerShare.vue'),
        meta: {
            breadcrumb: {
                parent: 'dashboard',
            },
        },
    },
    {
        path: '/opportunity/:id',
        name: 'opportunity-details',
        component: () => import(/* webpackChunkName: "asset" */ '@/views/careershares/CareerSharePage.vue'),
        meta: {
            breadcrumb: {
                parent: 'share-my-career',
            },
        },
    },
    {
        path: '/opportunity/:id/edit',
        name: 'edit-opportunity',
        component: () => import(/* webpackChunkName: "asset" */ '@/views/careershares/EditCareerShare.vue'),
        meta: {
            breadcrumb: {
                parent: 'opportunity-details',
            },
        },
    },
    {
        path: '/organization-settings',
        name: 'organization-settings',
        // route level code-splitting
        // this generates a separate chunk (about.[hash].js) for this route
        // which is lazy-loaded when the route is visited.
        component: () => import(/* webpackChunkName: "employer-user-management" */ '../views/admin/OrgSettings.vue'),
        meta: {
            breadcrumb: {
                parent: 'dashboard',
            },
        },
    },
    {
        path: '/user-management',
        name: 'user-management',
        // route level code-splitting
        // this generates a separate chunk (about.[hash].js) for this route
        // which is lazy-loaded when the route is visited.
        component: () => import(/* webpackChunkName: "employer-user-management" */ '../views/EmployerUserManagement.vue'),
        meta: {
            breadcrumb: {
                parent: 'dashboard',
            },
        },
    },
    {
        path: '/error',
        name: 'error',
        component: () => import(/* webpackChunkName: "error" */ '../views/Error.vue'),
    },
    {
        path: '/oauth2/authorized',
        name: 'oauth2-authorized',
        component: () => import(/* webpackChunkName: "authorized" */ '../views/GlobalPageSpinner.vue'),
    },
    {
        path: '/login',
        name: 'login',
        component: () => import(/* webpackChunkName: "login" */ '../views/LoginSelect.vue'),
    },
    {
        path: '/password-expired',
        name: 'password-expired',
        component: () => import(/* webpackChunkName: "login" */ '../views/PasswordExpiredPage.vue'),
    },
    {
        path: '/volunteers',
        name: 'browse-volunteers',
        component: () => import(/* webpackChunkName: "connectors" */ '../views/browse/BrowseConnectors.vue'),
        meta: {
            breadcrumb: {
                parent: 'dashboard',
            },
        },
    },
    {
        path: '/:org_id/volunteers',
        name: 'browse-org-volunteers',
        component: () => import(/* webpackChunkName: "connectors" */ '../views/browse/BrowseConnectors.vue'),
        meta: {
            breadcrumb: {
                parent: 'dashboard',
            },
        },
    },
    {
        path: '/organizations',
        name: 'browse-organizations',
        component: () => import(/* webpackChunkName: "connectors" */ '../views/browse/BrowseOrganizations.vue'),
        meta: {
            breadcrumb: {
                parent: 'dashboard',
            },
        },
    },
    {
        path: '/request/new/:templateId?',
        name: 'new-request',
        component: () => import(/* webpackChunkName: "connectors" */ '../views/requests/NewRequest.vue'),
        meta: {
            breadcrumb: {
                parent: 'connect-directory',
            },
        },
    },
    {
        path: '/request/v2/new',
        name: 'new-request-v2',
        component: () => import(/* webpackChunkName: "connectors" */ '../views/requests/RequestFormPage.vue'),
        meta: {
            breadcrumb: {
                parent: 'connect-directory',
            },
        },
    },
    {
        path: '/connect-directory',
        name: 'connect-directory',
        component: () => import(/* webpackChunkName: "connectors" */ '../views/requests/RequestDirectory.vue'),
        meta: {
            breadcrumb: {
                parent: 'dashboard',
            },
        },
    },
    {
        path: '/request/:id',
        name: 'request-details',
        meta: {
            segmentName: 'Request Details',
            breadcrumb: {
                parent: 'connect-directory',
            },
        },
        component: () => import(/* webpackChunkName: "connectors" */ '../views/requests/RequestDetails.vue'),
    },
    {
        path: '/request/:id/edit',
        name: 'request-edit',
        component: () => import(/* webpackChunkName: "connectors" */ '../views/requests/RequestEdit.vue'),
        meta: {
            breadcrumb: {
                parent: 'request-details',
            },
        },
    },
    {
        path: '/request/v2/:id/edit',
        name: 'request-edit-v2',
        component: () => import(/* webpackChunkName: "connectors" */ '../views/requests/RequestFormPage.vue'),
        meta: {
            breadcrumb: {
                parent: 'request-details',
            },
        },
    },
    {
        path: '/request/v2/:id/duplicate',
        name: 'request-duplicate',
        component: () => import(/* webpackChunkName: "connectors" */ '../views/requests/RequestFormPage.vue'),
        meta: {
            breadcrumb: {
                parent: 'request-details',
            },
        },
    },
    {
        path: '/settings',
        name: 'settings',
        component: () => import(/* webpackChunkName: "settings" */ '../views/Settings.vue'),
        meta: {
            breadcrumb: {
                parent: 'dashboard',
            },
        },
    },
    {
        path: '/direct-request/:id',
        name: 'direct-request',
        component: () => import(/* webpackChunkName: "connectors" */ '../views/educators/DirectRequest.vue'),
        meta: {
            breadcrumb: {
                parent: 'connect-directory',
            },
        },
    },
    {
        path: '/message',
        name: 'message',
        component: () => import(/* webpackChunkName: "connectors" */ '../views/Message.vue'),
        meta: {
            breadcrumb: {
                parent: 'dashboard',
            },
        },
    },

    // DE ADMIN VIEWS
    {
        path: '/org-management',
        name: 'org-management',
        component: () => import(/* webpackChunkName: "login" */ '../views/admin/OrgManagement.vue'),
        meta: {
            breadcrumb: {
                parent: 'dashboard',
            },
        },
    },
    {
        path: '/educator-management',
        name: 'educator-management',
        component: () => import(/* webpackChunkName: "login" */ '../views/admin/EducatorManagement.vue'),
        meta: {
            breadcrumb: {
                parent: 'dashboard',
            },
        },
    },
    {
        path: '/request-admin',
        name: 'request-admin',
        component: () => import(/* webpackChunkName: "login" */ '../views/admin/Request.vue'),
        meta: {
            breadcrumb: {
                parent: 'dashboard',
            },
        },
    },
    {
        path: '/test-asset',
        name: 'test-asset',
        component: () => import(/* webpackChunkName: "login" */ '../views/admin/AssetTest.vue'),
    },
    {
        path: '/feature-flags',
        name: 'feature-flags',
        component: () => import(/* webpackChunkName: "login" */ '../views/admin/FeatureFlags.vue'),
        meta: {
            breadcrumb: {
                parent: 'dashboard',
            },
        },
    },
    {
        path: '/groups',
        name: 'browse-groups',
        component: () => import(/* webpackChunkName: "admin" */ '../views/browse/BrowseGroups.vue'),
        meta: {
            breadcrumb: {
                parent: 'dashboard',
            },
        },
    },
    {
        path: '/group/new',
        name: 'new-group',
        component: () => import(/* webpackChunkName: "admin" */ '../views/admin/NewGroupPage.vue'),
        meta: {
            breadcrumb: {
                parent: 'org-management',
            },
        },
    },
    {
        path: '/group/:id/edit',
        name: 'edit-group',
        component: () => import(/* webpackChunkName: "admin" */ '../views/admin/EditGroupPage.vue'),
        meta: {
            breadcrumb: {
                parent: 'org-management',
            },
        },
    },
    {
        path: '/group/:id',
        name: 'group-details',
        component: () => import(/* webpackChunkName: "educator" */ '../views/groups/GroupDetailsView.vue'),
        meta: {
            breadcrumb: {
                parent: 'browse-organizations',
            },
        },
    },
    {
        path: '/group/:id/admins',
        name: 'group-admins',
        component: () => import(/* webpackChunkName: "admin" */ '../views/groups/GroupAdminsView.vue'),
        meta: {
            breadcrumb: {
                parent: 'browse-groups',
            },
        },
    },
    {
        path: '/group/:id/statistics',
        name: 'group-statistics',
        component: () => import(/* webpackChunkName: "admin" */ '../views/groups/GroupStatisticsView.vue'),
        meta: {
            breadcrumb: {
                parent: 'browse-groups',
            },
        },
    },
    {
        path: '/request-templates',
        name: 'request-templates',
        component: () => import(/* webpackChunkName: "login" */ '../views/browse/RequestTemplates.vue'),
        meta: {
            breadcrumb: {
                parent: 'dashboard',
            },
        },
    },
    {
        path: '/share-my-career',
        name: 'share-my-career',
        component: () => import(/* webpackChunkName: "shares" */ '../views/browse/BrowseCareerShares.vue'),
        meta: {
            breadcrumb: {
                parent: 'dashboard',
            },
        },
    },
    {
        path: '/how-it-works',
        name: 'how-it-works',
        component: () => import(/* webpackChunkName: "how-it-works" */ '../views/HowItWorks.vue'),
        meta: {
            breadcrumb: {
                parent: 'dashboard',
            },
        },
    },
];

export default routes;
